import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal as GoalModel } from '@feathr/blackbox';
import { Alert, Radios } from '@feathr/components';

import DonationFormsSelect from '../../DonationFormsSelect';
import { buildDonationFormSubmissionPredicates } from '../Goal.predicates';
import { useFormGoalSegment } from '../useFormGoalSegment';

import * as styles from './DonationFormSubmissionGoal.css';

interface IProps {
  goal: GoalModel;
}

function DonationFormSubmissionGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    spacing: { '4': gap },
  } = useMantineTheme();

  const { selectMode, selectedForms, handleSelectMode, handleFormSelection } = useFormGoalSegment({
    goal,
    buildPredicates: buildDonationFormSubmissionPredicates,
    segmentNamePrefix: 'Donation Form Goal',
  });

  const options = [
    { id: 'any', name: t('Any donation form submission') },
    { id: 'specific', name: t('Submissions on specific donation form(s)') },
  ];

  return (
    <Flex direction={'column'} gap={gap}>
      <Alert
        className={styles.alert}
        description={t(
          'The actual donation value submitted through the donation form will be used as the conversion value. You do not have the opportunity to set a custom conversion value.',
        )}
        title={t('Important')}
        type={'info'}
      />
      <Radios
        helpText={t('Choose which donation form submissions will be tracked as conversions.')}
        label={t('Donation form selection')}
        layout={'block'}
        onChange={handleSelectMode}
        options={options}
        value={selectMode}
      />
      {selectMode === 'specific' && (
        <DonationFormsSelect
          filters={{ state: 'published' }}
          isMulti={true}
          label={t('Donation form(s)')}
          onChange={handleFormSelection}
          placeholder={t('Start typing to search donation forms')}
          value={selectedForms}
        />
      )}
    </Flex>
  );
}

export default observer(DonationFormSubmissionGoal);
